import axios from "axios";

// Customer applications (the employee customer journey), the relationship
// check and the member search. Contract: snb_lightspeed_backend
// docs/customer-module/kyc/11-Slice-1-Contract.md sections D, F and H.1, and
// 13-Slice-2-Full-Replacement.md §4 (documents and group members).
//
// Every action resolves to the whole envelope (res.data) and rejects with what
// the global interceptor rejects with: the axios response, whose `data` is the
// envelope ({message, status_code, data}).

export default {
    namespaced: true,

    state: {
        applications: [],
        application: {},
    },

    getters: {
        applications(state) {
            return state.applications;
        },
        application(state) {
            return state.application;
        },
    },

    mutations: {
        SET_APPLICATIONS(state, applications) {
            return (state.applications = applications);
        },

        // A fresh read, or a reset ({}). For the application already loaded,
        // a read older than what is held (a lower lock_version) is dropped.
        SET_APPLICATION(state, application) {
            const current = state.application?.data;
            const incoming = application?.data;

            if (
                current &&
                incoming &&
                String(current.customer_application_id) === String(incoming.customer_application_id) &&
                Number(incoming.lock_version) < Number(current.lock_version)
            ) {
                return state.application;
            }

            return (state.application = application);
        },

        // The answer of a write on the loaded application. The workspace
        // mounts every tab and each autosaves on its own, so answers can
        // arrive out of order: one for another application than the loaded
        // one, or with a lower lock_version than the one held, is stale and
        // ignored.
        //
        // Autosave and the confirmation answer an ApplicationResource without
        // its contacts (contract K.1: contacts only on show, the contacts
        // routes and D.8-D.11). Keep the contacts already loaded for the same
        // application instead of dropping them.
        UPDATE_APPLICATION(state, envelope) {
            const current = state.application?.data;
            const incoming = envelope?.data;

            if (!current || !incoming) {
                return state.application;
            }
            if (String(current.customer_application_id) !== String(incoming.customer_application_id)) {
                return state.application;
            }
            if (Number(incoming.lock_version) < Number(current.lock_version)) {
                return state.application;
            }

            if (incoming.contacts === null || incoming.contacts === undefined) {
                incoming.contacts = current.contacts;
            }

            return (state.application = envelope);
        },
    },

    actions: {
        // D.2 the list and inbox
        async fetchApplications({ commit }, params) {
            return await axios
                .get("/api/v1/customer-applications", { params: params })
                .then((res) => {
                    commit("SET_APPLICATIONS", res.data);
                    return res.data;
                })
                .catch((err) => Promise.reject(err));
        },

        // D.3 one application, with contacts and flags
        async fetchApplication({ commit }, id) {
            return await axios
                .get(`/api/v1/customer-applications/${id}`)
                .then((res) => {
                    commit("SET_APPLICATION", res.data);
                    return res.data;
                })
                .catch((err) => Promise.reject(err));
        },

        clearApplication({ commit }) {
            commit("SET_APPLICATION", {});
        },

        // D.1 the "New member" dialog: {phone, phone_country, id_type, id_no}
        async createApplication({ commit }, payload) {
            return await axios
                .post("/api/v1/customer-applications", payload)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // D.4 autosave one section: {id, section, ...fields}
        async autosaveApplication({ commit }, { id, ...body }) {
            return await axios
                .patch(`/api/v1/customer-applications/${id}`, body)
                .then((res) => {
                    commit("UPDATE_APPLICATION", res.data);
                    return res.data;
                })
                .catch((err) => Promise.reject(err));
        },

        // doc 16 §4: pick up a USSD application from the queue
        // (GET /customer-applications?channel=ussd). {id} claims it for the
        // caller; {id, employee_id} gives it to that staff member (needs
        // "view member applications").
        async claimApplication({ commit }, { id, ...body }) {
            return await axios
                .post(`/api/v1/customer-applications/${id}/claim`, body)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // D.5 the contact set
        async fetchApplicationContacts({ commit }, id) {
            return await axios
                .get(`/api/v1/customer-applications/${id}/contacts`)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // D.5 replace the draft set: {id, next_of_kin: [], referees: []}
        async saveApplicationContacts({ commit }, { id, ...body }) {
            return await axios
                .put(`/api/v1/customer-applications/${id}/contacts`, body)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // D.6 send the member confirmation code
        async sendConfirmation({ commit }, id) {
            return await axios
                .post(`/api/v1/customer-applications/${id}/confirmation`)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // D.7 {id, code, declaration_accepted}
        async verifyConfirmation({ commit }, { id, ...body }) {
            return await axios
                .post(`/api/v1/customer-applications/${id}/confirmation/verify`, body)
                .then((res) => {
                    commit("UPDATE_APPLICATION", res.data);
                    return res.data;
                })
                .catch((err) => Promise.reject(err));
        },

        // D.8 submit
        async submitApplication({ commit }, id) {
            return await axios
                .post(`/api/v1/customer-applications/${id}/submit`)
                .then((res) => {
                    commit("UPDATE_APPLICATION", res.data);
                    return res.data;
                })
                .catch((err) => Promise.reject(err));
        },

        // D.9 {id, lock_version, flags_acknowledged?, note?, risk_rating?}.
        // lock_version is the one of the application on screen: the loaded
        // one, which only ever moves forward, else the caller's.
        async approveApplication({ state }, { id, ...body }) {
            const loaded = state.application?.data;
            const lockVersion =
                loaded && String(loaded.customer_application_id) === String(id) ? loaded.lock_version : body.lock_version;

            return await axios
                .post(`/api/v1/customer-applications/${id}/approve`, { ...body, lock_version: lockVersion })
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // D.10 {id, reason_code, reason}
        async rejectApplication({ commit }, { id, ...body }) {
            return await axios
                .post(`/api/v1/customer-applications/${id}/reject`, body)
                .then((res) => {
                    commit("UPDATE_APPLICATION", res.data);
                    return res.data;
                })
                .catch((err) => Promise.reject(err));
        },

        // D.11 {id, reason}
        async returnApplication({ commit }, { id, ...body }) {
            return await axios
                .post(`/api/v1/customer-applications/${id}/return`, body)
                .then((res) => {
                    commit("UPDATE_APPLICATION", res.data);
                    return res.data;
                })
                .catch((err) => Promise.reject(err));
        },

        // doc 13 §4: one entry per applicable document type
        async fetchApplicationDocuments({ commit }, id) {
            return await axios
                .get(`/api/v1/customer-applications/${id}/documents`)
                .then((res) => ({ ...res.data, data: res.data?.data ?? [] }))
                .catch((err) => Promise.reject(err));
        },

        // doc 13 §4 "Files": one file for one type, multipart {file, document_type_id},
        // optional Idempotency-Key header (a retry returns the first file).
        // 201, data = a file block with status 'pending'. onProgress(0..100) is optional.
        async uploadApplicationFile({ commit }, { id, documentTypeId, file, idempotencyKey, onProgress }) {
            const form = new FormData();
            form.append("file", file);
            form.append("document_type_id", documentTypeId);

            const config = { showLoader: false, headers: {} };
            if (idempotencyKey) config.headers["Idempotency-Key"] = idempotencyKey;
            if (typeof onProgress === "function") {
                config.onUploadProgress = (event) => {
                    if (event && event.total) onProgress(Math.round((event.loaded * 100) / event.total));
                };
            }

            return await axios
                .post(`/api/v1/customer-applications/${id}/files`, form, config)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // doc 13 §4: {id, documentTypeId, payload: {document_number, issued_at, expires_at, file_ids?}}.
        // The payload is sent as given: `file_ids` (the version's whole ordered
        // list) only when the caller puts it there; a locked type (the identity
        // type, a group's GROUP_CERT) takes {file_ids} alone.
        async saveApplicationDocument({ commit }, { id, documentTypeId, payload }) {
            return await axios
                .put(`/api/v1/customer-applications/${id}/documents/${documentTypeId}`, payload)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // doc 13 §4: removes the draft of one type (422 on the identity type)
        async removeApplicationDocument({ commit }, { id, documentTypeId }) {
            return await axios
                .delete(`/api/v1/customer-applications/${id}/documents/${documentTypeId}`)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // doc 13 §4: the draft set {members[], set_version_no, rules}
        async fetchGroupMembers({ commit }, id) {
            return await axios
                .get(`/api/v1/customer-applications/${id}/group-members`)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // doc 13 §4: {id, members: [{member_customer_id, office, is_signatory}]} replaces the draft set
        async saveGroupMembers({ commit }, { id, members }) {
            return await axios
                .put(`/api/v1/customer-applications/${id}/group-members`, { members })
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // H.1 GET /customers/search: returns the envelope, never commits, so that
        // two pickers on one page do not share results. An empty result arrives
        // without `data`, so `data` defaults to [].
        async searchCustomers({ commit }, params) {
            return await axios
                .get("/api/v1/customers/search", { params: params })
                .then((res) => ({ ...res.data, data: res.data?.data ?? [] }))
                .catch((err) => Promise.reject(err));
        },

        // GET /pending-counts: the count of each "Show" list of the Pending
        // page the caller may see, {counts: {approval?, drafts?, self?
        // (formerly ussd), self_drafts?, changes?, submitted?}, total} (backend
        // docs/customer-module/record/03-Pending-Page.md). Never commits.
        // params: member_type (individual|group), optional.
        async fetchPendingCounts(context, params = {}) {
            return await axios
                .get("/api/v1/pending-counts", { params })
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // F.1 {context_type, context_id, subject, phone?, phone_country?, id_no?}
        async checkRelationship({ commit }, payload) {
            return await axios
                .post("/api/v1/relationship-checks", payload)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },
    },
};
